'use client';
import React, { useState, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import { breakdownChildResponse } from '@functions/amplience-functions';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import Button from '@repo/ui/src/components/Button';
import { classNames } from '@repo/ui/src/utils/classNames';
import { Anchor } from '@repo/ui/src/components/Anchor';
import NavbarBrand from './_Brand';
import SitewideBanner from '@repo/ui/src/components/SiteWideBanner';
import MobileDrawer from './_Mobile';
import NavbarShoppingCart from './_ShoppingCart';
import UserAvatar from './_UserAvatar';
import NavbarSearch from './_Search';
import NavbarDropdownContent from './_DropdownContent';
import { NavHeadingLink, NavHeadingButton, ActiveDropdownLine } from './styles';
import HeaderPaddingWrapper from '@repo/ui/src/components/HeaderPaddingWrapper';

const Navbar = ({
  body,
  navbarBrand,
  sitewideBanner,
  staticNavLinks,
  searchLink,
  skipToMainContentButtonText,
  mainContentLabel,
  headerLabel,
  primaryNavigationLabel,
  mobileMenuButtonLabel,
  backToMainMenuLabel,
}) => {
  const [drawerName, setDrawerName] = useState('');
  const [drawer, setDrawer] = useState({
    0: false,
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
  });
  const [hoverDelayHandler, setHoverDelayHandler] = useState(null);
  const res = breakdownChildResponse(body);

  const handleMouseEnter = (anchor, open, name) => {
    setHoverDelayHandler(
      setTimeout(() => {
        toggle(anchor, open, name);
      }, 200),
    );
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverDelayHandler);
  };

  const handleEscKey = (event) => {
    if (event.keyCode === 27) {
      Object.keys(drawer).forEach((v) => (drawer[v] = false));
      setDrawer({ ...drawer });
      setDrawerName(null);
    }
  };

  const toggleDrawer = (anchor, open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    toggle(anchor, open);
    if (open) {
      document.addEventListener('keydown', handleEscKey, false);
    } else {
      document.removeEventListener('keydown', handleEscKey, false);
    }
  };

  const toggle = (anchor, open, name) => {
    Object.keys(drawer).forEach((v) => (drawer[v] = false));
    setDrawer({ ...drawer, [anchor]: open });
    setDrawerName(name);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener('keydown', handleEscKey, false);
    };
  }, []);

  const smAndDown = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const searchLinkObj = searchLink?.link ? buildLinkObj(searchLink.link) : null;

  return (
    <>
      <Button
        as={Anchor}
        link="#main-content"
        size="small"
        className={classNames(
          'tw-z-[1102] tw-absolute -tw-top-20 tw-transition-top tw-ease-in-out tw-duration-[250] focus:tw-top-1 tw-left-1',
        )}
      >
        {skipToMainContentButtonText || 'Skip to Main Content'}
      </Button>
      <header aria-label={headerLabel || 'Traeger Navigation and Utilities Header'}>
        {sitewideBanner?.map?.((banner, idx) => (
          <SitewideBanner key={idx} {...banner} staticNavLinks={staticNavLinks} />
        ))}
        <nav aria-label={primaryNavigationLabel || 'Primary'}>
          <HeaderPaddingWrapper className="tw-relative tw-flex md:tw-grid md:tw-grid-cols-[1fr_auto_1fr] md:tw-gap-8 tw-min-h-15 tw-items-center tw-bg-neutral-700 tw-z-[501] sm:tw-min-h-16">
            {smAndDown && (
              <MobileDrawer
                categories={res}
                topMobileNavLinks={staticNavLinks}
                mobileMenuButtonLabel={mobileMenuButtonLabel}
                backToMainMenuLabel={backToMainMenuLabel}
              />
            )}
            <NavbarBrand {...navbarBrand} />
            <Box flexGrow={1} id="navbarNav">
              {!smAndDown && (
                <Box
                  component="ul"
                  display="flex"
                  justifyContent="center"
                  sx={{ padding: 0, margin: 0, listStyle: 'none' }}
                  aria-current={drawerName}
                >
                  {res.map((dropdownCategory, i) => {
                    const titleUrl =
                      dropdownCategory.props &&
                      dropdownCategory.props.categoryLink &&
                      dropdownCategory.props.categoryLink.length
                        ? buildLinkObj(dropdownCategory.props.categoryLink[0])
                        : null;
                    return (
                      <React.Fragment key={dropdownCategory?.props?.dropdownName || i}>
                        {titleUrl && !dropdownCategory.props.links ? (
                          <NavHeadingLink
                            href={titleUrl.href}
                            onMouseEnter={() => handleMouseEnter(i, false)}
                            onMouseLeave={handleMouseLeave}
                          >
                            {dropdownCategory?.props?.dropdownName}
                          </NavHeadingLink>
                        ) : (
                          <li>
                            <NavHeadingButton
                              type="button"
                              aria-controls={dropdownCategory.props.dropdownName.toLowerCase()}
                              aria-expanded={drawer[i]}
                              onClick={toggleDrawer(i, !drawer[i])}
                              onMouseEnter={() =>
                                handleMouseEnter(i, true, dropdownCategory?.props?.dropdownName)
                              }
                              onMouseLeave={handleMouseLeave}
                              sx={{
                                color: (theme) =>
                                  drawer[i]
                                    ? theme.palette.primary.main
                                    : theme.palette.neutral[50],
                              }}
                            >
                              {dropdownCategory?.props?.dropdownName}
                            </NavHeadingButton>
                            <ActiveDropdownLine className={drawer[i] ? 'active' : ''} />
                            <NavbarDropdownContent
                              open={drawer[i]}
                              index={i}
                              dropdownCategory={dropdownCategory}
                              toggleDrawer={toggleDrawer}
                            />
                          </li>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
              )}
            </Box>
            <div className="tw-grid tw-grid-flow-col tw-gap-2 tw-auto-cols-min tw-w-max tw-items-center tw-justify-self-end">
              {searchLinkObj && (
                <NavbarSearch href={searchLinkObj.href} placeholder={searchLinkObj.text} />
              )}
              <UserAvatar />
              <NavbarShoppingCart />
            </div>
          </HeaderPaddingWrapper>
        </nav>
      </header>
      <div tabIndex="-1" id="main-content" aria-label={mainContentLabel || 'Main Content'} />
    </>
  );
};

export default Navbar;
